import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { servicesService } from '../services/services.service'
import type { ServiceRequestInput } from '../services/services-catalog'

export const serviceQueryKeys = {
  all: ['services'] as const,
  requests: (userId: string) => [...serviceQueryKeys.all, 'requests', userId] as const,
  request: (userId: string, id: string) =>
    [...serviceQueryKeys.requests(userId), 'detail', id] as const,
}

export function useServiceRequests() {
  const { user } = useAuth()
  const userId = user?.id ?? (isIsolatedPreviewBuild ? 'preview' : '')
  return useQuery({
    queryKey: serviceQueryKeys.requests(userId),
    queryFn: () => servicesService.listRequests(),
    enabled: Boolean(userId),
    staleTime: 20_000,
  })
}

export function useServiceRequest(id: string | undefined) {
  const { user } = useAuth()
  const userId = user?.id ?? (isIsolatedPreviewBuild ? 'preview' : '')
  return useQuery({
    queryKey: serviceQueryKeys.request(userId, id ?? ''),
    queryFn: () => servicesService.getRequest(id ?? ''),
    enabled: Boolean(userId && id),
    staleTime: 10_000,
  })
}

export function useCreateServiceRequest() {
  const { user } = useAuth()
  const userId = user?.id ?? (isIsolatedPreviewBuild ? 'preview' : '')
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: ServiceRequestInput) => servicesService.createRequest(input),
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: serviceQueryKeys.requests(userId) })
      queryClient.setQueryData(serviceQueryKeys.request(userId, request.id), request)
    },
  })
}

export function useCancelServiceRequest() {
  const { user } = useAuth()
  const userId = user?.id ?? (isIsolatedPreviewBuild ? 'preview' : '')
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => servicesService.cancelRequest(id),
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: serviceQueryKeys.requests(userId) })
      queryClient.setQueryData(serviceQueryKeys.request(userId, request.id), request)
    },
  })
}

export function useAcceptServiceQuote() {
  const { user } = useAuth()
  const userId = user?.id ?? (isIsolatedPreviewBuild ? 'preview' : '')
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => servicesService.acceptQuote(id),
    onSuccess: (request) => {
      void queryClient.invalidateQueries({ queryKey: serviceQueryKeys.requests(userId) })
      queryClient.setQueryData(serviceQueryKeys.request(userId, request.id), request)
    },
  })
}
